/**
 * Register of videos whose H.264 transcode is still running (#22523).
 *
 * **Why a module-level map and not the file document.** `videoTranscode` overwrites the canonical S3
 * object in place, which is what makes the transcode cheap: `QuoteFile` has no status field and there is
 * no server-side file-document write path at all. Nothing can therefore mark the document "processing",
 * and `processFile` invokes the task Lambda with `InvocationType: 'Event'` and returns `true` at once, so
 * the response carries no job id either. The browser that requested the transcode is the only party that
 * knows one is running, and this is where it writes that down.
 *
 * **What consumes it.** `useTranscodedVideoSrc` in `@paintscout/ui`. A `<video>` element that mounted
 * before the job finished keeps the HEVC bytes it already demuxed — reopening the dialog fixed it only
 * because that mounts a new element. The watcher polls the object, and reloads the element once
 * MediaConvert has rewritten it.
 *
 * Deliberately not persisted. A page reload discards the map, and that is correct: a reload mounts new
 * elements, which fetch the object again anyway.
 *
 * **Scope is one JavaScript realm — one tab.** Nothing here crosses tabs or windows. Inside a tab the map
 * is shared on purpose: the dialog preview, the gallery strip and the estimate row all render the same
 * object, and each watches it independently. The first to see the swap clears the entry; the others still
 * reload, because each holds its own copy of the request it started with.
 */

export interface PendingVideoTranscode {
  /**
   * Epoch ms when the transcode was requested.
   *
   * Two jobs: it bounds how long a watcher polls, and it dates the upload. MediaConvert can only have
   * rewritten the object *after* this instant, so a `Last-Modified` later than it proves the swap
   * happened — which is what lets a watcher that starts late still reach the right answer.
   */
  requestedAt: number
}

/**
 * How long a watcher keeps polling before it gives up.
 *
 * Generous on purpose. A phone clip transcodes in well under a minute, but MediaConvert queues jobs and
 * the queue is shared, so the tail is much longer than the median. Giving up is not a failure state: it
 * returns the file to the behaviour it had before this watcher existed, which is a video that plays once
 * the element is next remounted.
 */
export const VIDEO_TRANSCODE_WATCH_TIMEOUT_MS = 5 * 60 * 1000

const pending = new Map<string, PendingVideoTranscode>()

/**
 * Key on the object URL with any query string and fragment removed, so a caller holding the versioned
 * url the watcher produced (`?v=1`) or the first-frame fragment (`#t=0.001`) still finds its entry.
 */
function transcodeKey(src?: string): string | undefined {
  if (!src) return undefined
  return src.split('#')[0].split('?')[0]
}

/** Drop entries no watcher can still act on, so an upload-heavy session cannot grow this map forever. */
function pruneExpired(now: number): void {
  for (const [key, entry] of pending) {
    if (now - entry.requestedAt > VIDEO_TRANSCODE_WATCH_TIMEOUT_MS) pending.delete(key)
  }
}

/** Record that `src` has a transcode in flight. Called by `uploadFile` when it requests the task. */
export function markVideoTranscodePending(src?: string): void {
  const key = transcodeKey(src)
  if (!key) return

  const now = Date.now()
  pruneExpired(now)
  pending.set(key, { requestedAt: now })
}

/** The in-flight transcode for `src`, or undefined. Expired entries count as absent. */
export function getVideoTranscodePending(src?: string): PendingVideoTranscode | undefined {
  const key = transcodeKey(src)
  if (!key) return undefined

  const entry = pending.get(key)
  if (!entry) return undefined
  if (Date.now() - entry.requestedAt > VIDEO_TRANSCODE_WATCH_TIMEOUT_MS) {
    pending.delete(key)
    return undefined
  }
  return entry
}

/** Stop watching `src` — the object changed, or the watcher gave up. */
export function clearVideoTranscodePending(src?: string): void {
  const key = transcodeKey(src)
  if (key) pending.delete(key)
}
